import { formatDateToISOString } from '../../shared/helpers/date.func';
import {
	ApiBatchContact,
	ApiContact,
	ApiContactParticipant,
	ApiContactParticipantItem,
	ApiContactValidDateRangeResponse,
	ApiUpdateContactBody,
	CreateApiContactBody,
} from '../api-types/contact.api';
import {
	Contact,
	ContactParticipant,
	ContactParticipantItem,
	ContactValidDateRange,
	CreateContactBody,
	UpdateContactBody,
} from '../app-types/contact.app';
import {
	tranformApiContactPointToAppContactPoint,
	transformContactPointToApiContactPoint,
} from './contact-point.transform';
import {
	transformApiBatchContactTypeToAppContactType,
	transformApiContactTypeToAppContactType,
	transformAppContactTypeToApiContactType,
} from './contact-type.transform';
import {
	transformApiBatchDocumentFilesToAppDocumentFiles,
	transformApiDocumentFilesToAppDocumentFiles,
	transformAppDocumentFilesToApiDocumentFiles,
} from './document.transform';
import { transformApiInstitutionBranchItemToAppInstitutionBranchItem } from './instition.tranform';
import { transformApiTasksToAppTasks, transformAppTasksToApiTasks } from './task.transformer';

/**
 * Transforms an ApiContact object to an app-specific Contact object.
 * @description This function maps properties of the ApiContact to the Contact interface.
 * It is used to convert data received from the API into a format that the application can work with.
 * @param input ApiContact
 * @returns Contact
 */
export const transformApiContactToAppContact = (input: ApiContact): Contact => {
	return {
		id: input.id,
		anonym: input.anonym ?? false,
		remark: input.remark ?? '',
		confidential: input.confidential ?? false,
		keyword: input.keyword ?? '',
		contactType: transformApiContactTypeToAppContactType(input.contactType),
		contactedAt: new Date(input.contactedAt),
		participants: (input.participants?.participants ?? []).map(transformApiContactParticipantToAppContactParticipant),
		documents: transformApiDocumentFilesToAppDocumentFiles(input.documents ?? []),
		tasks: transformApiTasksToAppTasks(input.tasks ?? []),
		isFrozen: input.isFrozen ?? false,
		editableForDays: input.editableForDays,
		participantsCount: input.participantsCount,
		clientParticipated: input.clientParticipated ?? false,
	};
};

/**
 * Converts the reduced contact representation returned by the batch endpoint
 * into the existing Contact model.
 *
 * Fields not included in the batch response use placeholder values and must
 * not be interpreted as fully loaded contact data.
 */
export const transformApiBatchContactToAppContact = (input: ApiBatchContact): Contact => {
	return {
		id: input.contactId,
		anonym: input.anonym ?? false,
		remark: '',
		confidential: input.confidential ?? false,
		keyword: input.keyword ?? '',
		contactType: transformApiBatchContactTypeToAppContactType(input.contactType),
		contactedAt: new Date(input.contactedAt),
		participants: [],
		documents: transformApiBatchDocumentFilesToAppDocumentFiles(input.documents ?? []),
		tasks: [],
		isFrozen: input.isFrozen ?? false,
		editableForDays: input.editableForDays,
		participantsCount: input.participantsCount,
		clientParticipated: input.clientParticipated ?? false,
	};
};

export const transformApiBatchContactsToAppContacts = (input: ApiBatchContact[]): Contact[] => {
	return input.map(transformApiBatchContactToAppContact);
};

/**
 * Transforms an array of ApiContact objects to an array of app-specific Contact objects.
 * @description This function is useful when you need to convert multiple Contacts received from the API
 * into a format that the application can work with. It uses the `transformApiContactToAppContact`
 * function to transform each individual Contact.
 * @param input Array of ApiContact objects.
 * @returns Array of Contact objects.
 */
export const transformApiContactsToAppContacts = (input: ApiContact[]): Contact[] => {
	if (!input) return [];
	return input.map(transformApiContactToAppContact);
};

/**
 * Transforms an app-specific Contact object to an ApiContact object.
 * @description This function maps properties of the Contact to the ApiContact interface.
 * It is used to convert data from the application into a format that can be sent to the API.
 * @param input Contact
 * @returns ApiContact
 */
export const transformAppContactToApiContact = (input: Contact) => {
	const output: ApiContact = {
		id: input.id,
		anonym: input.anonym,
		remark: input.remark,
		confidential: input.confidential,
		keyword: input.keyword,
		contactType: transformAppContactTypeToApiContactType(input.contactType),
		contactedAt: formatDateToISOString(input.contactedAt)!,
		participants: {
			participants: (input.participants ?? []).map(transformAppContactParticipantToApiContactParticipant),
		},
		documents: input.documents ? transformAppDocumentFilesToApiDocumentFiles(input.documents) : [],
		tasks: input.tasks ? transformAppTasksToApiTasks(input.tasks) : [],
		isFrozen: input.isFrozen,
		editableForDays: input.editableForDays,
		participantsCount: input.participantsCount,
		clientParticipated: input.clientParticipated ?? false,
	};
	return output;
};

/**
 * Transforms an array of app-specific Contact objects to an array of ApiContact objects.
 * @description This function is useful when you need to convert multiple app-specific Contacts
 * into a format that can be sent to the API. It uses the `transformAppContactToApiContact`
 * function to transform each individual ApiContact.
 * @param input Array of Contact objects.
 * @returns Array of ApiContact objects.
 */
export const transformAppContactsToApiContacts = (input: Contact[]): ApiContact[] => {
	return input.map(transformAppContactToApiContact);
};

/**
 * Transforms an app-specific CreateContact object into an CreateApiContactBody.
 * @description This function maps properties of the CreateContact to the CreateApiContactBody interface.
 * It is used to convert data from the application into a format that can be sent to the API.
 * @param input CreateContact object.
 * @returns CreateApiContactBody.
 */
export const transformCreateContactToCreateApiContactBody = (input: CreateContactBody) => {
	const output: CreateApiContactBody = {
		anonym: input.anonym,
		remark: input.remark,
		confidential: input.confidential,
		keyword: input.keyword,
		contactType: transformAppContactTypeToApiContactType(input.contactType),
		contactedAt: formatDateToISOString(input.contactedAt) ?? '',
		participants: input.participants.map((p) => ({
			id: p.id,
			typeId: p.typeId,
		})),
		documentIds: input.documentIds,
		selectedTasksIds: input.selectedTasksIds,
	};
	return output;
};

export const transformAppUpdateContactBodyToApiContactBody = (input: UpdateContactBody): ApiUpdateContactBody => {
	return {
		id: input.id,
		anonym: input.anonym,
		confidential: input.confidential,
		contactedAt: formatDateToISOString(input.contactedAt) ?? '',
		contactType: input.contactType,
		keyword: input.keyword,
		documentIds: input.documentIds,
		participants: input.participants,
		remark: input.remark,
		selectedTasksIds: input.selectedTasksIds,
	};
};

export const transformApiParticipantItemToAppParticipantItem = (
	input: ApiContactParticipantItem,
): ContactParticipantItem => {
	return {
		id: input.id,
		firstName: input.firstName,
		name: input.name,
		personTypeId: input.personTypeId,
		personId: input.personId,
	};
};

export const transformApiContactParticipantToAppContactParticipant = (
	input: ApiContactParticipant,
): ContactParticipant => {
	return {
		item: transformApiParticipantItemToAppParticipantItem(input.item),
		personTypeId: input.personTypeId,
		contactPoints: (input.contactPoints ?? []).map(tranformApiContactPointToAppContactPoint),
		institutionBranch: input.institutionBranch
			? transformApiInstitutionBranchItemToAppInstitutionBranchItem(input.institutionBranch)
			: undefined,
	};
};

export const transformAppContactParticipantItemToApiContactParticipantItem = (
	input: ContactParticipantItem,
): ApiContactParticipantItem => {
	return {
		id: input.id,
		firstName: input.firstName,
		name: input.name,
		personTypeId: input.personTypeId,
		personId: input.personId,
	};
};

export const transformAppContactParticipantToApiContactParticipant = (
	input: ContactParticipant,
): ApiContactParticipant => {
	return {
		item: transformAppContactParticipantItemToApiContactParticipantItem(input.item),
		personTypeId: input.personTypeId ?? input.item?.personTypeId,
		contactPoints: (input.contactPoints || []).map(transformContactPointToApiContactPoint),
	};
};

export function transformApiContactDateRangeToAppContactDateRange(
	input: ApiContactValidDateRangeResponse,
): ContactValidDateRange {
	return {
		minStartDate: new Date(input.minStartDate),
		maxEndDate: new Date(input.maxEndDate),
	};
}
